export const POST_PERFORMANCE_RANK_METRICS = [
  { key: 'engagement', label: 'Engagement rate' },
  { key: 'likes', label: 'Likes' },
  { key: 'comments', label: 'Comments' },
  { key: 'shares', label: 'Shares' },
  { key: 'saves', label: 'Saves' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'postClicks', label: 'Post clicks' },
]

export const POST_TOPIC_RANK_METRICS = [
  { key: 'engagements', label: 'Engagements' },
  { key: 'engagement', label: 'Engagement rate' },
  { key: 'likes', label: 'Likes' },
  { key: 'comments', label: 'Comments' },
  { key: 'shares', label: 'Shares' },
  { key: 'saves', label: 'Saves' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'postClicks', label: 'Post clicks' },
]

export const POST_PERFORMANCE_GROUPS = [
  {
    value: 'top',
    label: 'Top 15',
    description: 'Highest-performing posts',
  },
  {
    value: 'bottom',
    label: 'Bottom 15',
    description: 'Lowest-performing posts',
  },
]

export const POST_TOPIC_PERFORMANCE_GROUPS = [
  {
    value: 'top',
    label: 'Top 15',
    description: 'Highest-performing topics',
  },
  {
    value: 'bottom',
    label: 'Bottom 15',
    description: 'Lowest-performing topics',
  },
]

export const POST_PERFORMANCE_LIMIT = 15

export function hasValidPostMetricValue(post, metricKey) {
  const value = post?.[metricKey]
  return typeof value === 'number' && Number.isFinite(value)
}

function getPostEngagementRateRatio(post) {
  const rate = post?.engagementRate ?? post?.engagement
  if (typeof rate !== 'number' || !Number.isFinite(rate)) return null
  return rate <= 1 ? rate : rate / 100
}

export function getPostEngagements(post) {
  if (typeof post?.engagements === 'number' && Number.isFinite(post.engagements)) {
    return post.engagements
  }
  return null
}

export function hasValidTopicMetricPosts(posts, metricKey) {
  if (metricKey === 'engagements') {
    return posts.some((post) => getPostEngagements(post) != null)
  }
  if (metricKey === 'engagement') {
    return posts.some((post) => getPostEngagementRateRatio(post) != null)
  }
  return posts.some((post) => hasValidPostMetricValue(post, metricKey))
}

/**
 * Apply dashboard-level filters before ranking.
 * Date range is expected to be applied upstream when postsData is built.
 */
export function filterEligiblePosts(
  posts,
  { platform = 'all', platformPulsePlatforms = new Set() } = {}
) {
  let filtered = Array.isArray(posts) ? posts : []

  if (platformPulsePlatforms.size > 0) {
    filtered = filtered.filter((post) => platformPulsePlatforms.has(post.platform))
  }

  if (platform !== 'all') {
    filtered = filtered.filter((post) => post.platform === platform)
  }

  return filtered
}

export function calculateTopicMetricValue(posts, metricKey) {
  if (!Array.isArray(posts) || posts.length === 0) return null

  if (metricKey === 'engagements') {
    let total = 0
    let hasAny = false
    posts.forEach((post) => {
      const engagements = getPostEngagements(post)
      if (engagements != null) {
        total += engagements
        hasAny = true
      }
    })
    return hasAny ? total : null
  }

  if (metricKey === 'engagement') {
    const validPosts = posts.filter((post) => getPostEngagementRateRatio(post) != null)
    if (validPosts.length === 0) return null
    const totalRatePercent = validPosts.reduce(
      (sum, post) => sum + getPostEngagementRateRatio(post) * 100,
      0,
    )
    return totalRatePercent / validPosts.length
  }

  const validPosts = posts.filter((post) => hasValidPostMetricValue(post, metricKey))
  if (validPosts.length === 0) return null

  const total = posts.reduce(
    (sum, post) => sum + (hasValidPostMetricValue(post, metricKey) ? post[metricKey] : 0),
    0
  )
  return total / posts.length
}

export function formatTopicMetricSummary(metricKey, value) {
  if (value == null || !Number.isFinite(value)) return '—'
  const config = POST_TOPIC_RANK_METRICS.find((metric) => metric.key === metricKey)
  const summaryLabel = config?.label || ''
  if (metricKey === 'engagement') {
    return `${summaryLabel} ${value.toFixed(1)}%`
  }
  if (metricKey === 'engagements') {
    return `${summaryLabel} ${Math.round(value).toLocaleString('en-US')}`
  }
  return `${summaryLabel} ${Math.round(value).toLocaleString('en-US')}`
}

function findLeadingPlatform(posts, metricKey) {
  const platformIds = [...new Set(posts.map((post) => post.platform).filter(Boolean))]
  let leadingPlatform = null
  let leadingValue = null

  platformIds.forEach((platformId) => {
    const platformPosts = posts.filter((post) => post.platform === platformId)
    const value = calculateTopicMetricValue(platformPosts, metricKey)
    if (value == null) return
    if (leadingValue == null || value > leadingValue) {
      leadingValue = value
      leadingPlatform = platformId
    }
  })

  return leadingPlatform
}

export function buildTopicPlatformBreakdown(posts, { platformFilter = 'all' } = {}) {
  const platformIds = platformFilter === 'all'
    ? [...new Set(posts.map((post) => post.platform).filter(Boolean))]
    : [platformFilter]

  return platformIds
    .map((platformId) => {
      const platformPosts = posts.filter((post) => post.platform === platformId)
      if (platformPosts.length === 0) return null

      const engagementRate = calculateTopicMetricValue(platformPosts, 'engagement')
      const linkClicks = platformPosts
        .filter((post) => hasValidPostMetricValue(post, 'postClicks'))
        .reduce((sum, post) => sum + post.postClicks, 0)

      return {
        platform: platformId,
        engagementRate,
        linkClicks,
        postCount: platformPosts.length,
      }
    })
    .filter(Boolean)
}

export function buildTopicRecords(eligiblePosts, { platformFilter = 'all' } = {}) {
  const topicMap = new Map()

  eligiblePosts.forEach((post) => {
    const topicName = post.topic || post.campaign
    if (!topicName) return
    if (!topicMap.has(topicName)) topicMap.set(topicName, [])
    topicMap.get(topicName).push(post)
  })

  return Array.from(topicMap.entries()).map(([topic, posts]) => ({
    topic,
    posts,
    postCount: posts.length,
    platformBreakdown: buildTopicPlatformBreakdown(posts, { platformFilter }),
  }))
}

/**
 * Rank across the complete eligible dataset, then return up to `limit` posts.
 * Null/undefined metric values are excluded from ranking (not treated as zero).
 */
export function rankPostsByMetric(
  posts,
  {
    metric = 'engagement',
    performanceGroup = 'top',
    limit = POST_PERFORMANCE_LIMIT,
  } = {}
) {
  const eligiblePosts = Array.isArray(posts) ? posts : []

  const metricEligiblePosts = eligiblePosts.filter((post) => hasValidPostMetricValue(post, metric))

  const sorted = [...metricEligiblePosts].sort((a, b) => {
    const aVal = a[metric]
    const bVal = b[metric]
    return performanceGroup === 'bottom' ? aVal - bVal : bVal - aVal
  })

  return {
    eligibleTotal: eligiblePosts.length,
    metricEligibleTotal: metricEligiblePosts.length,
    posts: sorted.slice(0, limit),
  }
}

export function rankTopicsByMetric(
  topicRecords,
  {
    metric = 'engagement',
    performanceGroup = 'top',
    limit = POST_PERFORMANCE_LIMIT,
    platformFilter = 'all',
  } = {}
) {
  const records = Array.isArray(topicRecords) ? topicRecords : []

  const rankedTopics = records
    .map((record) => {
      const metricValue = calculateTopicMetricValue(record.posts, metric)
      if (metricValue == null) return null

      return {
        ...record,
        metricValue,
        totalEngagements: calculateTopicMetricValue(record.posts, 'engagements'),
        engagementRate: calculateTopicMetricValue(record.posts, 'engagement'),
        leadingPlatform: platformFilter === 'all'
          ? findLeadingPlatform(record.posts, metric)
          : null,
        metricSummary: formatTopicMetricSummary(metric, metricValue),
      }
    })
    .filter(Boolean)

  const sorted = [...rankedTopics].sort((a, b) => (
    performanceGroup === 'bottom' ? a.metricValue - b.metricValue : b.metricValue - a.metricValue
  ))

  return {
    eligibleTotal: records.length,
    metricEligibleTotal: rankedTopics.length,
    topics: sorted.slice(0, limit),
  }
}

/**
 * Convenience wrapper for UI layers that pass filter options separately.
 */
export function getRankedPostPerformance(
  posts,
  {
    metric = 'engagement',
    performanceGroup = 'top',
    limit = POST_PERFORMANCE_LIMIT,
    platform = 'all',
    platformPulsePlatforms = new Set(),
  } = {}
) {
  const eligiblePosts = filterEligiblePosts(posts, { platform, platformPulsePlatforms })
  return rankPostsByMetric(eligiblePosts, { metric, performanceGroup, limit })
}

export function getRankedTopicPerformance(
  posts,
  {
    metric = 'engagement',
    performanceGroup = 'top',
    limit = POST_PERFORMANCE_LIMIT,
    platform = 'all',
    platformPulsePlatforms = new Set(),
  } = {}
) {
  const eligiblePosts = filterEligiblePosts(posts, { platform, platformPulsePlatforms })
  const topicRecords = buildTopicRecords(eligiblePosts, { platformFilter: platform })
  return rankTopicsByMetric(topicRecords, { metric, performanceGroup, limit, platformFilter: platform })
}
